// The wait page (doc/20261018-cloud-zone-browser.md §3.2.4, card 23): public, no sign-in. It reads the zone's
// state, wakes it by address when asked (or at once from Open zone), and opens it when it is awake. It shows
// nothing about the zone but its address and state.
import { ClockCircleOutlined, CloudOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { LanguageSwitch } from '../LanguageSwitch';
import { Button, Card, Progress, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../i18n/format';
import { controlApi } from './api';
import { safeReturnPath, TENANT_NAME, WAIT_POLL_MS, waitPollDelay } from './cloud';
import { BrassButton } from './CloudZone';
import '../styles/control.css';

const { Paragraph, Text, Title } = Typography;
/** A wake that has not answered awake within 2 minutes is shown as failed (card 23). */
const WAKE_GIVE_UP_MS = 2 * 60_000;
/**
 * A frozen zone's wake, before its restore shows (freeze/thaw spec §3.8): the thaw's start takes seconds, so 10 minutes
 * that still read frozen mean it did not start, or failed and went back to sleep. A restore that shows is never given up.
 */
const THAW_START_GIVE_UP_MS = 10 * 60_000;
const WAKE_SECONDS = 20;

type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'missing' }
  | { readonly kind: 'asleep' }
  | { readonly kind: 'waking'; readonly since: number }
  | { readonly kind: 'limited'; readonly until: number }
  | { readonly kind: 'failed' }
  | { readonly kind: 'opening' };

export function WaitPage() {
  const { t } = useTranslation('console');
  const format = useFormat();
  const params = new URLSearchParams(window.location.search);
  // eslint-disable-next-line i18next/no-literal-string -- query parameter names
  const tenant = params.get('tenant') ?? '';
  const valid = TENANT_NAME.test(tenant);
  // eslint-disable-next-line i18next/no-literal-string -- query parameter names
  const returnPath = safeReturnPath(params.get('return'));
  const auto = params.get('auto') === '1';
  const [phase, setPhase] = useState<Phase>(valid ? { kind: 'loading' } : { kind: 'missing' });
  const [webAddress, setWebAddress] = useState<string | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  // Why it is asleep or waking (freeze/thaw spec §3.8): `frozen` opens from its backup, `restoring` is doing so.
  const [reason, setReason] = useState<'restoring' | 'frozen' | null>(null);
  const [now, setNow] = useState(Date.now());
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // One wake in flight per page, as an attempt: its number and its abort. Open zone's auto wake happens once.
  const attempt = useRef<{ readonly id: number; readonly controller: AbortController } | null>(null);
  const attempts = useRef(0);
  const autoUsed = useRef(false);

  const open = useCallback((address: string) => {
    setPhase({ kind: 'opening' });
    // The host is always the answer's address; `return` is a checked path (§3.2.4).
    window.location.replace(`${address}${returnPath}`);
  }, [returnPath]);

  /**
   * Wake by address. Waking shows at once, and its 2 minutes start with the click: a request that never
   * answers ends as "didn't wake", aborted, and a later answer from an expired attempt changes nothing.
   */
  const wake = useCallback(async () => {
    if (attempt.current !== null) return;
    const mine = { id: attempts.current += 1, controller: new AbortController() };
    attempt.current = mine;
    setPhase({ kind: 'waking', since: Date.now() });
    const current = () => attempt.current?.id === mine.id;
    try {
      const answer = await controlApi.addressWake(tenant, mine.controller.signal);
      if (!current()) return;
      if (answer.status === 202) setUnreachable(false);
      else if (answer.status === 429) setPhase({ kind: 'limited', until: Date.now() + (answer.retryAfterSeconds ?? 60) * 1_000 });
      else if (answer.status === 404) setPhase({ kind: 'missing' });
      else { setUnreachable(true); setPhase({ kind: 'asleep' }); }
    } catch {
      if (current()) { setUnreachable(true); setPhase({ kind: 'asleep' }); }
    } finally {
      if (current()) attempt.current = null;
    }
  }, [tenant]);

  // The state read: every 3 s, slower when throttled; it never wakes anything.
  useEffect(() => {
    if (!valid) return;
    let stopped = false;
    let timer: number | undefined;
    let backoff: number | null = null;
    const read = async () => {
      let delay = WAIT_POLL_MS;
      try {
        const answer = await controlApi.wakeState(tenant);
        if (stopped) return;
        setUnreachable(false);
        if (answer.status === 429) {
          backoff = waitPollDelay(true, answer.retryAfterSeconds, backoff);
          delay = backoff;
        } else {
          backoff = null;
          if (answer.status === 404) { setPhase({ kind: 'missing' }); return; }
          const body = answer.body;
          if (answer.status === 200 && body !== null) {
            setWebAddress(body.webAddress);
            setReason(body.reason ?? null);
            const current = phaseRef.current;
            if (body.state === 'awake') { open(body.webAddress); return; }
            if (body.state === 'waking') {
              // A restore is still under way after the 2 minutes a wake is given: shown as such again.
              if (current.kind !== 'waking' && (current.kind !== 'failed' || body.reason === 'restoring')) setPhase({ kind: 'waking', since: Date.now() });
            } else if (current.kind === 'loading') {
              // Asleep: a link shows Wake and open, so a prefetcher never wakes a zone; Open zone wakes at once.
              setPhase({ kind: 'asleep' });
              if (auto && !autoUsed.current) { autoUsed.current = true; void wake(); }
            }
          } else setUnreachable(true);
        }
      } catch {
        if (!stopped) setUnreachable(true);
      }
      if (!stopped) timer = window.setTimeout(() => void read(), delay);
    };
    void read();
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [auto, open, tenant, valid, wake]);

  // The clock for the progress bar, the 2-minute limit and the limit's time.
  useEffect(() => {
    if (phase.kind !== 'waking' && phase.kind !== 'limited') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [phase.kind]);
  useEffect(() => {
    // A restore takes minutes, not seconds: it is never given up on here.
    if (phase.kind !== 'waking' || reason === 'restoring' || now - phase.since < (reason === 'frozen' ? THAW_START_GIVE_UP_MS : WAKE_GIVE_UP_MS)) return;
    // The attempt is over: its request, if still open, is abandoned, and Try again may start another.
    attempt.current?.controller.abort();
    attempt.current = null;
    setPhase({ kind: 'failed' });
  }, [now, phase, reason]);

  const host = webAddress === null ? tenant : new URL(webAddress).host;
  let content: React.ReactNode;
  switch (phase.kind) {
    case 'missing':
      content = <><div className="cp-auth-icon"><ExclamationCircleOutlined /></div>
        <Title level={2}>{t('wait.missingTitle')}</Title>
        <Paragraph type="secondary">{t('wait.missing')}</Paragraph></>;
      break;
    case 'asleep':
      content = <><div className="cp-auth-icon"><CloudOutlined /></div>
        <Title level={2}>{t('wait.asleepTitle', { host })}</Title>
        <Paragraph type="secondary">{reason === 'frozen' ? t('wait.asleepFrozen') : t('wait.asleep', { seconds: WAKE_SECONDS })}</Paragraph>
        <BrassButton size="large" block onClick={() => void wake()}>{t('wait.wakeAndOpen')}</BrassButton></>;
      break;
    case 'waking': {
      // A frozen zone opens from its backup: its wake reads as the restore it starts.
      if (reason === 'restoring' || reason === 'frozen') {
        content = <><div className="cp-auth-icon"><LoadingOutlined /></div>
          <Title level={2}>{t('wait.restoringTitle', { host })}</Title>
          <Paragraph type="secondary">{t('wait.restoring')}</Paragraph>
          <Progress percent={100} showInfo={false} status="active" />
          <Paragraph type="secondary" className="cp-wait-note">{t('wait.restoringNote')}</Paragraph></>;
        break;
      }
      const percent = Math.min(95, Math.round((now - phase.since) / 1_000 / WAKE_SECONDS * 100));
      content = <><div className="cp-auth-icon"><LoadingOutlined /></div>
        <Title level={2}>{t('wait.wakingTitle', { host })}</Title>
        <Paragraph type="secondary">{t('wait.waking', { seconds: WAKE_SECONDS })}</Paragraph>
        <Progress percent={percent} showInfo={false} status="active" />
        <Paragraph type="secondary" className="cp-wait-note">{t('wait.wakingNote')}</Paragraph></>;
      break;
    }
    case 'limited':
      content = <><div className="cp-auth-icon"><ClockCircleOutlined /></div>
        <Title level={2}>{t('wait.limitedTitle')}</Title>
        <Paragraph type="secondary">{t('wait.limited', { time: format.date(new Date(phase.until), { hour: '2-digit', minute: '2-digit' }) })}</Paragraph>
        {webAddress !== null && <Button size="large" block onClick={() => window.location.assign(`${webAddress}${returnPath}`)}>{t('wait.openAnyway')}</Button>}
        {now >= phase.until && <Button type="link" onClick={() => void wake()}>{t('wait.wakeNow')}</Button>}</>;
      break;
    case 'failed':
      content = <><div className="cp-auth-icon"><ExclamationCircleOutlined /></div>
        <Title level={2}>{t('wait.failedTitle')}</Title>
        <Paragraph type="secondary">{reason === 'frozen' ? t('wait.failedFrozen', { host }) : t('wait.failed', { host })}</Paragraph>
        <Button type="primary" size="large" block onClick={() => void wake()}>{t('common.tryAgain')}</Button></>;
      break;
    default:
      content = <><div className="cp-auth-icon"><LoadingOutlined /></div>
        <Title level={2}>{phase.kind === 'opening' ? t('wait.opening', { host }) : host}</Title></>;
  }
  return <main className="cp-auth">
    <div className="cp-auth-theme"><LanguageSwitch className="cp-theme-button" /></div>
    {/* eslint-disable-next-line i18next/no-literal-string -- the product name */}
    <a className="cp-auth-brand" href="/" aria-label={t('shell.home')}><span className="cp-mark">L</span><span>LexSide</span></a>
    <Card className="cp-auth-card">{content}
      {unreachable && phase.kind !== 'missing' && <Paragraph type="secondary" className="cp-wait-note">{t('wait.unreachable')}</Paragraph>}
    </Card>
    <Text type="secondary" className="cp-auth-foot">{t('wait.foot')}</Text>
  </main>;
}
